import { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import LocalizedStrings from 'react-localization';
import {
    BaseLocaleContentType,
    LocaleReducerFunction,
    FormatAsStringFunction,
    FormatInvalidFunction,
    FormatRequiredFunction,
    LocalizationContextState,
    LocaleContent,
    LocaleMap,
    Locales,
    LocalizationDispatch,
    LocalizationDispatchContextType,
    LocalizationDispatchTypes,
    LocalizationProviderComponent,
    LocalizationReducerFunction,
    LocalizationState,
    LocalizationStateContextType,
    LocalizationType,
    UseClearLocaleHook,
    UseLocaleHook,
    UseLocalizationDispatchHook,
    UseLocalizationExtensionHook,
    UseLocalizationHook,
    UseLocalizationDispatchReturn,
    LoanerOrRentalLocalizationTextHook,
} from './localization.types';
import clone from 'just-clone';
import { LoanerOrRentalOption } from '../../API/Enums';

/**
 * This object exists primarily to initialize the Localization object with the default english localization and no
 * other optional localizations. This allows us to then later enable or disable other localizations that are not English.
 */
export const baseLocaleContent: LocaleContent = {
    [Locales.en]: LocaleMap[Locales.en],
};

/**
 * We have to force Typescript to import all available types from Locales.en.
 * If we use type LocaleContent directly, Typescript will union all locales and only give us
 * values that are defined across all locales.
 */
const Localization: LocalizationType = new LocalizedStrings(baseLocaleContent as BaseLocaleContentType);

/**
 * The localization context reducer that takes dispatch action either individually or an array of dispatches
 * and ensures the update the current state of the context.
 *
 * @param state - {@link LocalizationReducerFunction state} description.
 * @param action - {@link LocalizationReducerFunction action} description.
 */
export const localizationReducer: LocalizationReducerFunction = (state, actions) => {
    let tempState: LocalizationContextState = state;

    for (const action of actions) {
        switch (action.type) {
            case LocalizationDispatchTypes.SetLocale:
                action.payload ??= initialState.locale;

                if (tempState.locale !== action.payload) {
                    tempState.Localization.setLanguage(action.payload);

                    tempState = {
                        ...tempState,
                        locale: action.payload,
                    };
                }
                break;
            case LocalizationDispatchTypes.Clear:
                tempState.Localization.setLanguage(initialState.locale);

                tempState = {
                    ...initialState,
                    Localization: tempState.Localization,
                };
                break;
            case LocalizationDispatchTypes.EnableLocale: {
                const currentLocales: Locales[] = tempState.Localization.getAvailableLanguages() as Locales[];
                const localeKeys: Locales[] = Object.keys(LocaleMap) as Locales[];

                const enableLocaleReducer: LocaleReducerFunction = (previousContent, locale) => {
                    const hasNotAddedLocaleContent: boolean = !(locale in previousContent);
                    const isFirstLocaleInCurrentLocales: boolean = currentLocales.indexOf(locale) > -1;

                    if (hasNotAddedLocaleContent && (isFirstLocaleInCurrentLocales || locale === action.payload)) {
                        switch (locale) {
                            case Locales.en:
                                previousContent[locale] = LocaleMap[locale];
                                break;
                            case Locales.es:
                                previousContent[locale] = LocaleMap[locale];
                                break;
                            case Locales.fr:
                                previousContent[locale] = LocaleMap[locale];
                                break;
                            case Locales.none:
                                previousContent[locale] = LocaleMap[locale];
                                break;
                            default:
                                break;
                        }
                    }

                    return previousContent;
                };

                const newContent: LocaleContent = localeKeys.reduce<LocaleContent>(enableLocaleReducer, clone(baseLocaleContent));
                const newLocalization: LocalizationType = new LocalizedStrings(newContent) as LocalizationType;

                tempState = {
                    ...tempState,
                    Localization: newLocalization,
                };
                break;
            }
            case LocalizationDispatchTypes.DisableLocale: {
                const availableLocales: Locales[] = tempState.Localization.getAvailableLanguages() as Locales[];

                const disableLocaleReducer: LocaleReducerFunction = (previousContent, locale) => {
                    if (locale !== action.payload) {
                        switch (locale) {
                            case Locales.en:
                                previousContent[locale] = LocaleMap[locale];
                                break;
                            case Locales.es:
                                previousContent[locale] = LocaleMap[locale];
                                break;
                            case Locales.fr:
                                previousContent[locale] = LocaleMap[locale];
                                break;
                            case Locales.none:
                                previousContent[locale] = LocaleMap[locale];
                                break;
                            default:
                                break;
                        }
                    }

                    return previousContent;
                };

                const newContent: LocaleContent = availableLocales.reduce(disableLocaleReducer, clone(baseLocaleContent));
                const newLocalization: LocalizationType = new LocalizedStrings(newContent) as LocalizationType;

                tempState = {
                    ...tempState,
                    Localization: newLocalization,
                };
                break;
            }
            default:
                break;
        }
    }

    return tempState;
};

const initialState: LocalizationContextState = {
    Localization: Localization,
    locale: Locales.en,
};

const LocalizationStateContext: LocalizationStateContextType = createContext<LocalizationState>(undefined);
const LocalizationDispatchContext: LocalizationDispatchContextType = createContext<LocalizationDispatch>(undefined);

/**
 * The localization provider ensures the entire application uses the correct localization for the
 * text displayed.
 *
 * @param children - {@link LocalizationProviderComponent children} description.
 */
export const LocalizationProvider: LocalizationProviderComponent = ({
    children,
}) => {
    const [state, dispatch] = useReducer<LocalizationReducerFunction>(localizationReducer, initialState);

    return (
        <LocalizationStateContext.Provider value={state}>
            <LocalizationDispatchContext.Provider value={dispatch}>
                {children}
            </LocalizationDispatchContext.Provider>
        </LocalizationStateContext.Provider>
    );
};

/**
 * Localization context hook to retrieve the current locale.
 */
export const useLocale: UseLocaleHook = () => {
    const context: LocalizationState = useContext(LocalizationStateContext);

    if (context === undefined) {
        throw new Error('useLocale must be used within a LocalizationProvider');
    }

    return context.Localization.getLanguage() as Locales;
};

/**
 * Localization context hook to retrieve the current localization object.
 */
export const useLocalization: UseLocalizationHook = () => {
    const context: LocalizationState = useContext(LocalizationStateContext);

    if (context === undefined) {
        throw new Error('useLocalization must be used within a LocalizationProvider');
    }

    return context.Localization;
};

/**
 * Localization context hook that returns a dispatch function
 * to dispatch actions to update the current state.
 *
 * @returns `localizationDispatch` - {@link UseLocalizationDispatchReturn localizationDispatch} description.
 */
export const useLocalizationDispatch: UseLocalizationDispatchHook = () => {
    const context: LocalizationDispatch = useContext(LocalizationDispatchContext);

    if (context === undefined) {
        throw new Error('useLocalizationDispatch must be used within a LocalizationProvider');
    }

    const localizationDispatch: UseLocalizationDispatchReturn = useCallback((...args) => {
        context(args);
    }, [context]);

    return localizationDispatch;
};

/**
 * Localization context hook that returns an function to clear the current
 * locale if the locale is not `en-us`.
 *
 * @returns `clearLocale` - {@link UseClearLocaleHook clearLocale} description.
 */
export const useClearLocale: UseClearLocaleHook = () => {
    const dispatch: UseLocalizationDispatchReturn = useLocalizationDispatch();
    const locale: Locales = useLocale();

    /**
     * Returned by the `useClearLocale` hook from the localization context. Clears the locale
     * if the current locale is not `en-us`.
     */
    const clearLocale: VoidFunction = useCallback(() => {
        if (initialState.locale !== locale) {
            dispatch({ type: LocalizationDispatchTypes.Clear });
        }
    }, [dispatch, locale]);

    return useMemo(() => ({
        clearLocale,
    }), [clearLocale]);
};

/**
 * Localization context hook that returns helper functions and localized strings
 * for invalid fields.
 *
 * @note For more information on the returned values, click {@link UseLocalizationExtensionHook here}.
 */
export const useLocalizationExtension: UseLocalizationExtensionHook = () => {
    const Localization: LocalizationType = useLocalization();

    /**
     * Format the passed string replacing the numbered placeholders
     *  i.e. "I'd like some {0} and {1}", or just "{0}"
     *
     * @example strings.formatString(strings.question, strings.bread, strings.butter)
     */
    const formatAsString: FormatAsStringFunction = useCallback((str, ...values) => {
        return Localization.formatString<string | number>(str, ...values)
            .toString();
    }, [Localization]);

    /**
     * Returns a localized message stating the field is required
     */
    const formatRequired: FormatRequiredFunction = useCallback((field) => {
        return formatAsString(Localization.IsRequired, field);
    }, [Localization.IsRequired, formatAsString]);

    /**
     * Returns a localized message stating the field is invalid
     */
    const formatInvalid: FormatInvalidFunction = useCallback((field) => {
        if (field === 'Kiosk') {
            return formatAsString(Localization.NotValidForThisKioskType);
        }
        else {
            return formatAsString(Localization.PleaseEnterAValid, field);
        }
    }, [Localization.NotValidForThisKioskType, Localization.PleaseEnterAValid, formatAsString]);

    return useMemo(() => ({
        formatAsString,
        formatRequired,
        formatInvalid,
        phoneInvalid: Localization.PhoneInvalid,
        emailInvalid: Localization.EmailInvalid,
        zipCodeInvalid: Localization.ZipCodeInvalid,
        postalCodeInvalid: Localization.PostalCodeCanadaError,
        vinInvalid: Localization.VinInvalid,
        vinInvalidFullOrLast8: Localization.VinInvalidFullOrLast8,
        emailOrPhoneInvalid: Localization.EmailOrPhoneInvalid,
    }), [
        Localization.EmailInvalid,
        Localization.EmailOrPhoneInvalid,
        Localization.PhoneInvalid,
        Localization.PostalCodeCanadaError,
        Localization.VinInvalid,
        Localization.VinInvalidFullOrLast8,
        Localization.ZipCodeInvalid,
        formatAsString,
        formatInvalid,
        formatRequired,
    ]);
};

/**
 * Localization context hook that returns the correct localized text for the
 * exchange key process instructions for a customer that will be picking up
 * their key and exchanging it for a rental or loaner vehicle key.
 *
 * @note For more information regarding the return object, click {@link LoanerOrRentalLocalizationTextHook here}.
 *
 * @param `loanerOrRentalOption` - {@link LoanerOrRentalLocalizationTextHook loanerOrRentalOption} description.
 */
export const useLoanerOrRentalLocalizationText: LoanerOrRentalLocalizationTextHook = ({
    loanerOrRentalOption,
}) => {
    const Localization = useLocalization();

    switch (loanerOrRentalOption) {
        case LoanerOrRentalOption.Both:
            return {
                headerText: Localization.LoanerAndRentalReturn,
                instructionText: Localization.LoanerOrRental.toLowerCase(),
            };
        case LoanerOrRentalOption.Loaners:
            return {
                headerText: Localization.LoanerReturn,
                instructionText: Localization.Loaner.toLowerCase(),
            };
        case LoanerOrRentalOption.Rentals:
            return {
                headerText: Localization.RentalReturn,
                instructionText: Localization.Rental.toLowerCase(),
            };
        default:
            return {
                headerText: undefined,
                instructionText: undefined,
            };
    }
};
